import { useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid, type GridColDef, type GridRenderCellParams } from '@mui/x-data-grid';
import DeleteIcon from '@mui/icons-material/Delete';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useCatalog, useWishlist, useAddToWishlist, useRemoveFromWishlist } from '../hooks/useWishlist';
import { usePermission } from '../hooks/usePermission';
import { useConfirm } from '../context/ConfirmDialogContext';
import { useSnackbar } from '../context/SnackbarContext';
import type { WishlistEntry, CatalogEntry } from '../api/wishlist';

const SERVER_TYPE_COLOR: Record<string, 'default' | 'success' | 'warning' | 'info'> = {
  native: 'success',
  wine: 'warning',
  java: 'info',
};

export function Wishlist() {
  const [selected, setSelected] = useState<CatalogEntry | null>(null);
  const canManage = usePermission('wishlist.manage');
  const confirm = useConfirm();
  const showSnackbar = useSnackbar();

  const { data: catalog } = useCatalog();
  const { data: wishlist, isLoading } = useWishlist();
  const addMutation = useAddToWishlist();
  const removeMutation = useRemoveFromWishlist();

  const wishlistAppIds = new Set((wishlist ?? []).map((w) => w.appid));
  const availableCatalog = (catalog ?? []).filter((c) => !wishlistAppIds.has(c.appid));

  async function handleAdd() {
    if (!selected) return;
    try {
      await addMutation.mutateAsync(selected.appid);
      showSnackbar(`"${selected.title}" added to wishlist`, 'success');
      setSelected(null);
    } catch {
      showSnackbar('Failed to add to wishlist', 'error');
    }
  }

  async function handleRemove(entry: WishlistEntry) {
    const ok = await confirm(`Remove "${entry.title}" from the wishlist?`, { title: 'Remove from wishlist' });
    if (!ok) return;
    try {
      await removeMutation.mutateAsync(entry.appid);
      showSnackbar(`"${entry.title}" removed`, 'success');
    } catch {
      showSnackbar('Failed to remove from wishlist', 'error');
    }
  }

  const columns: GridColDef<WishlistEntry>[] = [
    { field: 'appid', headerName: 'AppID', width: 100, renderCell: (p: GridRenderCellParams<WishlistEntry>) => (
      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{p.value as number}</Typography>
    )},
    { field: 'title', headerName: 'Game', flex: 1 },
    { field: 'description', headerName: 'Description', flex: 2,
      valueGetter: (_v, row) => row.description,
      renderCell: (p: GridRenderCellParams<WishlistEntry>) => (
        <Tooltip title={p.value as string}>
          <Typography variant="body2" noWrap>{p.value as string}</Typography>
        </Tooltip>
      )},
    { field: 'server_type', headerName: 'Type', width: 110, renderCell: (p: GridRenderCellParams<WishlistEntry>) => (
      <Chip
        label={p.value as string}
        size="small"
        color={SERVER_TYPE_COLOR[p.value as string] ?? 'default'}
      />
    )},
    { field: 'added_at', headerName: 'Added', width: 170,
      valueGetter: (_v, row) => new Date(row.added_at).toLocaleDateString() },
    { field: 'store_url', headerName: 'Store', width: 70, sortable: false,
      renderCell: (p: GridRenderCellParams<WishlistEntry>) => (
        <Tooltip title="Open Steam store page">
          <IconButton size="small" component="a" href={p.value as string} target="_blank" rel="noopener">
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )},
    ...(canManage ? [{
      field: 'actions',
      headerName: '',
      width: 80,
      sortable: false,
      renderCell: (p: GridRenderCellParams<WishlistEntry>) => (
        <IconButton size="small" color="error" onClick={() => handleRemove(p.row)}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      ),
    } as GridColDef<WishlistEntry>] : []),
  ];

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 3 }}>Steam Dedicated Server Wishlist</Typography>

      {canManage && (
        <Stack direction="row" spacing={2} sx={{ mb: 3 }} alignItems="flex-start">
          <Autocomplete
            options={availableCatalog}
            getOptionLabel={(o) => `${o.title} (${o.appid})`}
            value={selected}
            onChange={(_e, v) => setSelected(v)}
            sx={{ width: 360 }}
            renderInput={(params) => <TextField {...params} label="Add game from catalog" size="small" />}
            renderOption={(props, o) => (
              <li {...props} key={o.appid}>
                <Stack>
                  <Typography variant="body2">{o.title}</Typography>
                  <Typography variant="caption" color="text.secondary">{o.appid} · {o.server_type}</Typography>
                </Stack>
              </li>
            )}
          />
          <Button
            variant="contained"
            disabled={!selected || addMutation.isPending}
            onClick={handleAdd}
          >
            Add to Wishlist
          </Button>
        </Stack>
      )}

      {isLoading && <CircularProgress />}
      {wishlist && wishlist.length === 0 && (
        <Typography color="text.secondary">No games on the wishlist yet.</Typography>
      )}
      {wishlist && wishlist.length > 0 && (
        <DataGrid
          rows={wishlist}
          columns={columns}
          getRowId={(r) => r.appid}
          density="compact"
          disableRowSelectionOnClick
          pageSizeOptions={[25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          sx={{ bgcolor: 'background.paper' }}
        />
      )}
    </Box>
  );
}
